import { useWeb3React } from '@web3-react/core';
import { formatEther, formatUnits, isHexString, parseEther } from 'ethers/lib/utils';
import { useState } from 'react';
import useSWR from 'swr';
import { config } from '../../../config/config';
import {
    EnabledChains,
    OrderBookParamStructs,
    OrderBookType,
    OrderDataTypes,
    PositionTransaction,
} from '../../../config/constants';
import {
    isAlterPositionOrder,
    isClosePositionOrder,
    generateAdjustPositionLog,
    verifyChainConfig,
    generateDepositPositionLog,
    generateWithdrawPositionLog,
    generateSizePositionLog,
    isPendingOrder,
    subgraphQuery,
} from '../../../helpers';
import useTokenDetails from '../useTokenDetails';
import useTradePools from './useTradePools';
import useUserOrders from './useUserOrders';
import { BigNumber } from 'ethers';
import moment from 'moment';
import { TransactionLog } from '../../../context/transaction/transactionTypes';

const ROW_PER_LOAD = 100;

const useTransactionLog = () => {
    const { account, chainId } = useWeb3React();
    const [pagesLoaded, setPagesLoaded] = useState(1);
    const [hasNextPage] = useState(true);

    const { userOrders } = useUserOrders();
    const { tradePools } = useTradePools();

    const { decimals } = useTokenDetails(
        config.addresses[chainId as EnabledChains[number]]?.collateral
    );

    const { data, error, mutate } = useSWR(
        account && chainId && decimals && userOrders && tradePools
            ? [
                  'transactionLog',
                  account,
                  chainId,
                  decimals,
                  pagesLoaded,
                  userOrders,
                  tradePools,
              ]
            : null,
        async (
            _,
            userAddress,
            chainId,
            decimals,
            pagesLoaded,
            userOrders,
            tradePools
        ) => {
            if (!verifyChainConfig(chainId)) return;

            const maxTransactionTimestamp = moment()
                .subtract(3, 'days')
                .startOf('day')
                .unix();

            const subgraphTransactions: PositionTransaction[] = await getAllTransactions(
                userAddress,
                ROW_PER_LOAD,
                chainId,
                maxTransactionTimestamp
            );

            const slicedOrders = userOrders
                .filter(
                    ({ status, updatedAt }) =>
                        !isPendingOrder(status) &&
                        moment
                            .unix(updatedAt)
                            .isAfter(moment.unix(maxTransactionTimestamp))
                )
                .sort((a, b) => {
                    const aTimestamp = Math.max(a.createdAt, a.updatedAt);
                    const bTimestamp = Math.max(b.createdAt, b.updatedAt);

                    return bTimestamp - aTimestamp;
                })
                .slice(0, ROW_PER_LOAD * pagesLoaded);

            const transactionLogs: (TransactionLog | null)[] = await Promise.all(
                slicedOrders.map(
                    async ({
                        status,
                        isMarketOrder,
                        isShort,
                        orderJson,
                        orderType,
                        txHash,
                        type,
                        updatedAt,
                        id,
                        sigHash,
                    }) => {
                        let leverage: string = '0',
                            volume: string = '0',
                            margin: string | undefined,
                            fees: string | undefined,
                            newLeverage: string | undefined,
                            newMargin: string | undefined,
                            newVolume: string | undefined,
                            estimatedPnl: string | undefined;

                        const {
                            params,
                            constraints,
                        }: OrderDataTypes<OrderBookParamStructs> = JSON.parse(orderJson);

                        const orderTimestamp = updatedAt;

                        const tradePair = tradePools.find(
                            ({ address }) =>
                                address.toLowerCase() ===
                                params.tradePair.toString().toLowerCase()
                        );

                        if (!tradePair) return null;

                        if (status === 'executed') {
                            const transactionFound = subgraphTransactions.find(
                                ({ txHash: subgraphTxHash }) => subgraphTxHash === txHash
                            );

                            if (!transactionFound) return null;

                            leverage = formatUnits(
                                transactionFound.entryLeverageBefore,
                                decimals
                            );

                            if (orderType === OrderBookType.OPEN) {
                                margin = formatUnits(
                                    transactionFound.marginBefore,
                                    decimals
                                );
                            }
                            volume = formatUnits(transactionFound.volumeBefore, decimals);
                            fees = formatUnits(transactionFound.feeAmount, decimals);

                            if (
                                orderType !== OrderBookType.OPEN &&
                                orderType !== OrderBookType.PARTIALLY_CLOSE &&
                                !isClosePositionOrder(orderType)
                            ) {
                                newLeverage = formatUnits(
                                    transactionFound.leverageAfter,
                                    decimals
                                );
                            }

                            if (isAlterPositionOrder(orderType)) {
                                newMargin = formatUnits(
                                    transactionFound.marginAfter,
                                    decimals
                                );
                            }

                            if (
                                isClosePositionOrder(orderType) ||
                                orderType === OrderBookType.PARTIALLY_CLOSE
                            ) {
                                if (transactionFound.trade) {
                                    estimatedPnl = formatUnits(
                                        transactionFound.trade.PnL,
                                        decimals
                                    );
                                } else {
                                    estimatedPnl = '0';
                                }
                            }

                            if (orderType === OrderBookType.EXTEND) {
                                newVolume = formatUnits(
                                    transactionFound.volumeAfter,
                                    decimals
                                );
                            }
                        } else {
                            if ('positionId' in params) {
                                // because details of positions can differ in time,
                                // we use timestamp to map the closest transaction and use the values as state

                                const userPositionTransactions =
                                    await fetchTransactionLog(
                                        userAddress,
                                        1000,
                                        chainId,
                                        0,
                                        undefined,
                                        `${params.tradePair
                                            .toString()
                                            .toLowerCase()}-${params.positionId.toString()}`
                                    );

                                const lastSuccessTransaction =
                                    userPositionTransactions.reduce(
                                        (closestTransaction, transaction) => {
                                            // filter transaction by ID
                                            if (!closestTransaction.timestamp)
                                                return transaction;

                                            const positionId =
                                                transaction.id.split('-')[1];

                                            if (
                                                positionId !==
                                                    params.positionId.toString() ||
                                                transaction.timestamp > orderTimestamp
                                            )
                                                return closestTransaction;

                                            // for when initialState is empty

                                            // gets the closest transaction timestamp to timestamp of order
                                            const curTimestampDiff =
                                                orderTimestamp - transaction.timestamp;

                                            const accTimestampDiff =
                                                orderTimestamp -
                                                closestTransaction.timestamp;

                                            return curTimestampDiff > accTimestampDiff
                                                ? closestTransaction
                                                : transaction;
                                        },
                                        {} as PositionTransaction
                                    );

                                if (!lastSuccessTransaction.id) return null;

                                leverage = formatUnits(
                                    lastSuccessTransaction.leverageBefore,
                                    decimals
                                );

                                margin = formatUnits(
                                    lastSuccessTransaction.marginBefore,
                                    decimals
                                );

                                volume = formatUnits(
                                    lastSuccessTransaction.volumeBefore,
                                    decimals
                                );

                                if (isAlterPositionOrder(orderType)) {
                                    if (
                                        orderType === OrderBookType.EXTEND_LEVERAGE &&
                                        'targetLeverage' in params
                                    ) {
                                        ({ newMargin, newLeverage } =
                                            generateAdjustPositionLog(
                                                margin,
                                                volume,
                                                BigNumber.from(100_00),
                                                params
                                            ));
                                    }

                                    if (
                                        orderType === OrderBookType.ADD_MARGIN &&
                                        'addedMargin' in params
                                    ) {
                                        ({ newMargin, newLeverage } =
                                            generateDepositPositionLog(
                                                margin,
                                                volume,
                                                decimals,
                                                params
                                            ));
                                    }

                                    if (
                                        orderType === OrderBookType.REMOVE_MARGIN &&
                                        'removedMargin' in params
                                    ) {
                                        ({ newMargin, newLeverage } =
                                            generateWithdrawPositionLog(
                                                margin,
                                                volume,
                                                BigNumber.from(100_00),
                                                decimals,
                                                params
                                            ));
                                    }

                                    if (
                                        orderType === OrderBookType.EXTEND &&
                                        'addedLeverage' in params
                                    ) {
                                        ({ newMargin, newVolume, newLeverage, fees } =
                                            generateSizePositionLog(
                                                margin,
                                                volume,
                                                BigNumber.from(100_00),
                                                decimals,
                                                params
                                            ));
                                    }
                                }

                                fees = undefined;
                                estimatedPnl = undefined;
                            } else {
                                // failed / cancelled open orders
                                leverage = BigNumber.from(params.leverage)
                                    .div(config.leverageMultiplier)
                                    .toString();

                                margin = formatUnits(params.margin.toString(), decimals);
                                volume = formatEther(
                                    parseEther(leverage)
                                        .mul(parseEther(margin))
                                        .div(parseEther('1'))
                                );

                                fees = undefined;
                            }
                        }

                        // backend may send an error string instead of hash.
                        if (txHash && !isHexString(txHash, 32)) {
                            txHash = '';
                        }

                        return {
                            orderId: id,
                            date: orderTimestamp,
                            isMarketOrder,
                            isShort,
                            leverage,
                            orderType: type ? type : orderType,
                            status,
                            tradePair,
                            volume,
                            deadline: Number(constraints.deadline.toString()),
                            estimatedPnl,
                            fees,
                            margin,
                            newLeverage,
                            newMargin,
                            newVolume,
                            txHash,
                            sigHash,
                        };
                    }
                )
            );

            const filteredLog = transactionLogs
                .filter(
                    (transaction): transaction is TransactionLog => transaction !== null
                )
                .sort((a, b) => b.date - a.date);

            return {
                transactionsToday: filteredLog.filter(({ date }) =>
                    moment.unix(date).isAfter(moment().subtract(1, 'day'))
                ),
                transactionYesterday: filteredLog.filter(
                    ({ date }) =>
                        moment.unix(date).isBefore(moment().subtract(1, 'day')) &&
                        moment.unix(date).isAfter(moment().subtract(2, 'day'))
                ),
                transactionTwoDaysAgo: filteredLog.filter(({ date }) =>
                    moment.unix(date).isBefore(moment().subtract(2, 'day'))
                ),
            };
        },
        {
            dedupingInterval: 0,
            refreshInterval: 0,
        }
    );

    const getQuery = (
        first: number,
        skip: number,
        userAddress: string,
        timestamp?: number,
        positionId?: string
    ) => `
        positionTransactions(
            first: ${first}, 
            skip: ${skip},
            orderDirection: desc
            orderBy: timestamp
            where: {
                user: "${userAddress.toLowerCase()}"
                ${positionId ? `,position: "${positionId}"` : ''}
                ${timestamp ? `,timestamp_gte: ${timestamp}` : ''}
            }
        ) {
            entryLeverageAfter
            entryMarginAfter
            entryLeverageBefore
            entryPriceAfter
            entryMarginBefore
            entryPriceBefore
            feeAmount
            id
            leverageAfter
            leverageBefore
            marginAfter
            marginBefore
            timestamp
            transactionType
            txHash
            volumeAfter
            volumeBefore
            position {
                realizedPnL
            }
            trade{
                PnL
            }
        }
    `;

    const fetchTransactionLog = async (
        userAddress: string,
        rowPerPage: number,
        chainId: number,
        skip: number = 0,
        timestamp?: number,
        positionId?: string
    ) => {
        const query = getQuery(rowPerPage, skip, userAddress, timestamp, positionId);

        const data: {
            positionTransactions: PositionTransaction[];
        } = await subgraphQuery(query, { chainId });

        return data.positionTransactions;
    };

    const getAllTransactions = async (
        userAddress: string,
        rowPerPage: number,
        chainId: number,
        timestamp: number
    ) => {
        const allTransactions: PositionTransaction[] = [];

        while (true) {
            const transactions = await fetchTransactionLog(
                userAddress,
                rowPerPage,
                chainId,
                allTransactions.length,
                timestamp
            );

            allTransactions.push(...transactions);

            if (!transactions.length || transactions.length < rowPerPage) {
                break;
            }
        }

        return allTransactions;
    };

    const handleLoadMore = async () => {
        if (!hasNextPage) return;

        setPagesLoaded((prev) => prev + 1);

        await mutate();
    };

    const updateTransactionLog = async () => {
        const interval = setInterval(() => {
            mutate();
        }, 1500);

        setTimeout(() => {
            clearInterval(interval);
        }, 25000);
    };

    return {
        transactionLog: data,
        isLoading: !data && !error,
        error,
        hasNextPage,
        handleLoadMore,
        updateTransactionLog,
    };
};

export default useTransactionLog;
